import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from "@reduxjs/toolkit";
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { database, storage, functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import {
  ref as firebaseStorageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import logger from "../../utils/logger";
import { serialize, deserialize } from "./utils";

const initialState = {
  performers: {},
  performersLoading: false,
  performersFetched: false,
  performersOrganizationId: null,
  performerSaveLoading: false,
  unreviewedDraftsCount: 0,
  unreviewedDraftsOrganizationId: null,
};

const performersSlice = createSlice({
  name: "performers",
  initialState,
  reducers: {
    clearPerformers(state) {
      state.performers = {};
      state.performersFetched = false;
      state.performersOrganizationId = null;
      state.unreviewedDraftsCount = 0;
      state.unreviewedDraftsOrganizationId = null;
    },
    setUnreviewedDraftsCount(state, action) {
      const { count, organizationId } = action.payload ?? {};
      state.unreviewedDraftsCount = count ?? 0;
      state.unreviewedDraftsOrganizationId = organizationId ?? null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPerformers.fulfilled, (state, action) => {
        if (action.payload) {
          const { performers, organizationId } = action.payload;
          state.performers = performers;
          state.performersLoading = false;
          state.performersFetched = true;
          state.performersOrganizationId = organizationId;
        }
      })
      .addCase(fetchPerformers.pending, (state, { meta }) => {
        const { organizationId } = meta.arg || {};
        if (organizationId && state.performersOrganizationId !== organizationId) {
          state.performers = {};
          state.performersFetched = false;
        }
        state.performersLoading = true;
      })
      .addCase(fetchPerformers.rejected, (state) => {
        state.performersLoading = false;
      })
      .addCase(createPerformer.fulfilled, (state, action) => {
        if (action.payload?.performer) {
          const { id, ...data } = action.payload.performer;
          state.performers[id] = serialize({ id, ...data });
          state.performerSaveLoading = false;
        }
      })
      .addCase(createPerformer.pending, (state) => {
        state.performerSaveLoading = true;
      })
      .addCase(createPerformer.rejected, (state) => {
        state.performerSaveLoading = false;
      })
      .addCase(updatePerformer.fulfilled, (state, action) => {
        if (action.payload?.performer) {
          const { id, ...data } = action.payload.performer;
          state.performers[id] = serialize({ id, ...data });
          state.performerSaveLoading = false;
        }
      })
      .addCase(updatePerformer.pending, (state) => {
        state.performerSaveLoading = true;
      })
      .addCase(updatePerformer.rejected, (state) => {
        state.performerSaveLoading = false;
      })
      .addCase(deletePerformer.fulfilled, (state, action) => {
        if (action.meta.arg?.performerId) {
          delete state.performers[action.meta.arg.performerId];
        }
      })
      .addCase(applyPerformerDraft.fulfilled, (state, action) => {
        if (action.payload?.performer) {
          const { id, ...data } = action.payload.performer;
          state.performers[id] = serialize({ id, ...data });
        }
      })
      .addCase(dismissPerformerDraft.fulfilled, (state, action) => {
        if (action.payload?.performer) {
          const { id, ...data } = action.payload.performer;
          state.performers[id] = serialize({ id, ...data });
        }
      });
  },
});

export const { clearPerformers, setUnreviewedDraftsCount } =
  performersSlice.actions;

/**
 * Subscribes to the count of performers with a pending draft (unreviewed) for an organization.
 * Returns an unsubscribe function. Dispatch with the store (e.g. in useEffect when org is set).
 */
export const beginListeningToUnreviewedDraftsCount = (organizationId) => (dispatch) => {
  const performersRef = collection(
    database,
    "organizations",
    organizationId,
    "performers",
  );
  const q = query(
    performersRef,
    where("pendingDraft", "!=", null),
  );
  return onSnapshot(
    q,
    (snapshot) => {
      dispatch(
        setUnreviewedDraftsCount({ count: snapshot.size, organizationId }),
      );
    },
    (err) => {
      logger.info("onSnapshot unreviewed drafts count error", organizationId, err);
      dispatch(setUnreviewedDraftsCount({ count: 0, organizationId }));
    },
  );
};

export const fetchPerformers = createAsyncThunk(
  "performers/fetchPerformers",
  async (params) => {
    const { organizationId } = params;
    const performersRef = collection(
      database,
      "organizations",
      organizationId,
      "performers",
    );
    const snapshot = await getDocs(performersRef);
    const performers = {};
    snapshot.docs.forEach((d) => {
      performers[d.id] = { id: d.id, ...d.data() };
    });
    return { performers: serialize(performers), organizationId };
  },
);

const defaultPerformerFields = {
  name: "",
  email: "",
  links: [],
  bio: "",
  credits: "",
  headshotUrl: null,
  headshotStoragePath: null,
};

export const createPerformer = createAsyncThunk(
  "performers/createPerformer",
  async (params) => {
    const { organizationId, values } = params;
    const callable = httpsCallable(functions, "createPerformer");
    const result = await callable({
      organizationId,
      values: {
        ...defaultPerformerFields,
        ...values,
        links: values.links ?? [],
      },
    });
    const data = result.data;
    return { performer: data?.performer };
  },
);

export const updatePerformer = createAsyncThunk(
  "performers/updatePerformer",
  async (params) => {
    const { organizationId, performerId, values } = params;
    const callable = httpsCallable(functions, "updatePerformer");
    const result = await callable({
      organizationId,
      performerId,
      values,
    });
    const data = result.data;
    return { performer: data?.performer };
  },
);

export const deletePerformer = createAsyncThunk(
  "performers/deletePerformer",
  async (params) => {
    const { organizationId, performerId } = params;
    const docRef = doc(
      database,
      "organizations",
      organizationId,
      "performers",
      performerId,
    );
    await deleteDoc(docRef);
  },
);

const HEADSHOT_FILENAME = "headshot";

export const uploadPerformerHeadshot = createAsyncThunk(
  "performers/uploadPerformerHeadshot",
  async (params, { getState, dispatch }) => {
    const { organizationId, performerId, file } = params;
    const ext = file.name.split(".").pop() || "jpg";
    const filename = `${HEADSHOT_FILENAME}.${ext}`;
    const storagePath = `images/organizations/${organizationId}/performers/${performerId}/${filename}`;
    const storageRef = firebaseStorageRef(storage, storagePath);
    await uploadBytes(storageRef, file);
    const downloadUrl = await getDownloadURL(storageRef);
    await dispatch(
      updatePerformer({
        organizationId,
        performerId,
        values: {
          headshotUrl: downloadUrl,
          headshotStoragePath: storagePath,
        },
      }),
    );
    return { downloadUrl, performerId };
  },
);

export const sendPerformerEditLinkEmail = createAsyncThunk(
  "performers/sendPerformerEditLinkEmail",
  async (params) => {
    const { organizationId, performerId } = params;
    const baseUrl =
      process.env.REACT_APP_BASE_URL || "https://www.comedyticketing.com";
    const callable = httpsCallable(functions, "sendPerformerEditLink");
    const result = await callable({
      organizationId,
      performerId,
      baseUrl,
    });
    return result.data;
  },
);

export const createPerformerEditLink = createAsyncThunk(
  "performers/createPerformerEditLink",
  async (params) => {
    const { organizationId, performerId } = params;
    const baseUrl =
      process.env.REACT_APP_BASE_URL || "https://www.comedyticketing.com";
    const callable = httpsCallable(functions, "createPerformerEditLink");
    const result = await callable({
      organizationId,
      performerId,
      baseUrl,
    });
    return result.data;
  },
);

export const applyPerformerDraft = createAsyncThunk(
  "performers/applyPerformerDraft",
  async (params) => {
    const { organizationId, performerId } = params;
    const callable = httpsCallable(functions, "applyPerformerDraft");
    const result = await callable({ organizationId, performerId });
    return result.data;
  },
);

export const dismissPerformerDraft = createAsyncThunk(
  "performers/dismissPerformerDraft",
  async (params) => {
    const { organizationId, performerId } = params;
    const callable = httpsCallable(functions, "dismissPerformerDraft");
    const result = await callable({ organizationId, performerId });
    return result.data;
  },
);

export const performersSliceSelector = createSelector(
  [(state) => state.performers],
  (performersState) => {
    const s = performersState ?? {};
    return {
      ...s,
      performers: deserialize(s.performers ?? {}),
    };
  },
);

/** Badge count for unreviewed performer drafts (current org only). */
export const selectUnreviewedDraftsCount = createSelector(
  [
    (state) => state.performers?.unreviewedDraftsCount ?? 0,
    (state) => state.performers?.unreviewedDraftsOrganizationId ?? null,
    (state) => state.organizations?.currentOrganizationId ?? null,
  ],
  (count, draftsOrgId, currentOrgId) =>
    currentOrgId && draftsOrgId === currentOrgId ? count : 0,
);

export default performersSlice.reducer;
